import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import AthleteMap from "./AthleteMap";

const API = process.env.REACT_APP_BACKEND_URL;
const EVENT_START = "2026-05-01";

const fmt = (iso) => new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
const km = (m) => (m / 1000).toFixed(1);
const eur = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function AthletePage() {
  const { athleteId } = useParams();

  const [athlete, setAthlete] = useState(null);
  const [activities, setActivities] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState([]);

  const [donations, setDonations] = useState([]);
  const [eventRoutes, setEventRoutes] = useState([]);
  const [orgLid, setOrgLid] = useState(null); // org-lid gekoppeld aan deze atleet
  const [csrfToken, setCsrfToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [donorAmount, setDonorAmount] = useState("");
  const [donorMessage, setDonorMessage] = useState("");
  const [donating, setDonating] = useState(false);
  const [donateError, setDonateError] = useState("");

  const activityCache = useRef({});  // athleteId → { filtered: [], all: [] }


  useEffect(() => {
    fetch(`${API}/athlete/public/${athleteId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) { setNotFound(true); setLoading(false); return; }
        setAthlete(data);
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });

    fetch(`${API}/athlete/${athleteId}/donations`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setDonations(Array.isArray(data) ? data : []))
      .catch(() => { });

    fetch(`${API}/api/csrf-token`, { credentials: "include" })
      .then(r => r.json())
      .then(data => setCsrfToken(data.csrfToken))
      .catch(() => { });

    // Zoek of dit atleet ook in de organisatie zit met een eigen (langere) tekst
    fetch(`${API}/organizations`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return;
        const lid = data.find(l => l.athleteId?._id === athleteId || l.athleteId === athleteId);
        if (lid?.story) setOrgLid(lid);
      })
      .catch(() => { });
  }, [athleteId]);

  // TODO: is this duplicated with Athletes (see below)
  // Load relevant event routes once athlete data is available
  useEffect(() => {
    if (!athlete) return;
    if (athlete.participationType !== "full" && athlete.participationType !== "partial") return;

    fetch(`${API}/event-routes`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        const relevant = athlete.participationType === "partial"
          ? data.filter(r => (athlete.participationDays || []).includes(r.dayNumber))
          : data;
        setEventRoutes(relevant);
      })
      .catch(() => { });
  }, [athlete]);

  useEffect(() => {
    if (!athleteId) { setSelectedActivities([]); return; }

    const cacheKey = `${athleteId}-${showAll ? "all" : "filtered"}`;
    if (activityCache.current[cacheKey]) {
      setSelectedActivities(activityCache.current[cacheKey]);
      return;
    }

    setLoadingDetail(true);
    const url = showAll
      ? `${API}/activities?athleteId=${athleteId}`
      : `${API}/activities?athleteId=${athleteId}&after=${EVENT_START}`;

    fetch(url)
      .then(r => r.json())
      .then(data => {
        const activities = Array.isArray(data) ? data : [];
        activityCache.current[cacheKey] = activities;
        setSelectedActivities(activities);
      })
      .catch(console.error)
      .finally(() => setLoadingDetail(false));
  }, [athleteId, showAll]);

  const submitDonation = async () => {
    const parsed = parseFloat(donorAmount.replace(",", "."));
    if (!parsed || parsed <= 0) { setDonateError("Vul een geldig bedrag in"); return; }
    if (!donorEmail.trim()) { setDonateError("Vul je e-mailadres in"); return; }
    setDonating(true);
    setDonateError("");
    try {
      const res = await fetch(`${API}/athlete/${athleteId}/donations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "CSRF-Token": csrfToken },
        body: JSON.stringify({ donorName, donorEmail, amount: parsed, message: donorMessage })
      });
      if (!res.ok) {
        const d = await res.json();
        setDonateError(d.error || "Donatie mislukt");
        return;
      }
      const data = await res.json();
      if (data.paymentUrl) {
        window.location.href = data.paymentUrl; // → Mollie betaalpagina
        return;
      }
      // Fallback als Mollie niet geconfigureerd is (dev zonder API-key)
      setDonations(prev => [data, ...prev]);
      setDonorName("");
      setDonorEmail("");
      setDonorAmount("");
      setDonorMessage("");
    } catch {
      setDonateError("Donatie mislukt");
    } finally {
      setDonating(false);
    }
  };

  if (loading) return <div className="pane"><p>Laden…</p></div>;
  if (notFound) return (
    <div className="pane">
      <p>Deelnemer niet gevonden. <Link to="/athletes">Terug naar overzicht</Link></p>
    </div>
  );

  const donationTotal = donations.reduce((s, d) => s + d.amount, 0);
  const progressPct = athlete.targetAmount > 0
    ? Math.min(100, (donationTotal / athlete.targetAmount) * 100)
    : 0;

  const showMap = selectedActivities.length > 0 || eventRoutes.length > 0;

  const trainings = selectedActivities.filter(a => a.isTraining);
  const events = selectedActivities.filter(a => !a.isTraining);


  return (
    <div className="pane">
      <Link to="/athletes" className="back-link">← Overzicht</Link>

      {/* ── Header ── */}
      <div className="ap-header">
        {athlete.profilePhoto && (
          <img src={athlete.profilePhoto} alt={athlete.firstName} className="ap-photo" />
        )}
        <div className="ap-header-info">
          <h1 className="ap-name">{athlete.name || `${athlete.firstName} ${athlete.lastName}`}</h1>
          {athlete.participationType === "full" && <p className="ap-badge">Doet mee aan de hele route</p>}
          {athlete.participationType === "partial" && <p className="ap-badge">Doet mee aan deel van de route</p>}
          {athlete.participationType === "individual" && <p className="ap-badge">Individuele acties</p>}
        </div>
      </div>

      {/* ── Donatiebalk ── */}
      {athlete.targetAmount > 0 && (
        <div className="ap-goal">
          <div className="ap-goal-bar-track">
            <div className="ap-goal-bar-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="ap-goal-amounts">
            <span className="ap-goal-raised">{eur(donationTotal)} opgehaald</span>
            <span className="ap-goal-target">Doel: {eur(athlete.targetAmount)}</span>
          </div>
        </div>
      )}

      {/* ── Bio ── */}
      {athlete.bio && (
        <p className="athlete-bio" dangerouslySetInnerHTML={{ __html: athlete.bio }} />
      )}

      {/* ── Link naar uitgebreidere tekst in organisatie-pagina ── */}
      {orgLid && orgLid.story && orgLid.story !== athlete.bio && (
        <p className="athlete-bio-org-link">
          <Link to={`/organisatie#lid-${orgLid._id}`} className="org-card-link">
            Lees meer over {athlete.firstName} op de organisatiepagina →
          </Link>
        </p>
      )}

      {/* ── Kaart ── */}
      {showMap && (
        <section className="ap-section">
          <AthleteMap activities={selectedActivities} eventRoutes={eventRoutes} />
        </section>
      )}

      {/* ── Activiteiten ── */}

      <section className="ap-section">
        <h2>Activiteiten</h2>

        <div className="detail-filter-bar">
          {!showAll ? (
            <p className="note">
              Activiteiten vanaf {new Date(EVENT_START).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}.{" "}
              <button className="btn-link" onClick={() => setShowAll(true)}>
                Toon alle activiteiten (kan trager laden)
              </button>
            </p>
          ) : (
            <p className="note">
              Alle activiteiten worden getoond.{" "}
              <button className="btn-link" onClick={() => setShowAll(false)}>Toon alleen actieperiode</button>
            </p>
          )}
        </div>

        {selectedActivities.length === 0 ? (
          <p className="note">Nog geen activiteiten gevonden vanaf {new Date(EVENT_START).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}.</p>
        ) : (
          <table className="activity-table">
            <tbody>
              {selectedActivities.map(a => (
                <tr key={a._id}>
                  <td>{a.name}</td>
                  <td>{fmt(a.date)}</td>
                  <td>{km(a.distance)} km</td>
                  <td><span className={`ap-activity-type ${a.isTraining ? "training" : "event"}`}>{a.isTraining ? "training" : "evenement"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Donaties ── */}
      <section className="ap-section">
        <h2>Donaties</h2>

        {donations.length === 0 ? (
          <p className="note">Nog geen donaties geregistreerd.</p>
        ) : (
          <table className="activity-table ap-donations-table">
            <tbody>
              {donations.map(d => (
                <tr key={d._id}>
                  <td><strong>{d.donorName || "Anoniem"}</strong>{d.message && <><br /><small>{d.message}</small></>}</td>
                  <td>{fmt(d.date)}</td>
                  <td className="ap-donation-amount">{eur(d.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="ap-donate-form">
          <h3>Doneer</h3>
          <div className="ap-donate-fields">
            <input
              value={donorName}
              onChange={e => setDonorName(e.target.value)}
              placeholder="Jouw naam (optioneel)"
            />
            <input
              type="email"
              required
              value={donorEmail}
              onChange={e => setDonorEmail(e.target.value)}
              placeholder="Jouw e-mailadres (verplicht)"
            />
            <div className="ap-amount-row">
              <span className="ap-eur">€</span>
              <input
                type="number"
                min="1"
                step="1"
                value={donorAmount}
                onChange={e => setDonorAmount(e.target.value)}
                placeholder="Bedrag"
                className="ap-amount-input"
              />
            </div>
            <textarea
              value={donorMessage}
              onChange={e => setDonorMessage(e.target.value)}
              placeholder="Bericht (optioneel)"
              rows={2}
              className="ap-message"
            />
          </div>
          {donateError && <p className="content-error">{donateError}</p>}
          <button onClick={submitDonation} disabled={donating}>
            {donating ? "Bezig…" : "Doneer"}
          </button>
        </div>
      </section>
    </div>
  );
}
